import { FC, useEffect, useMemo } from 'react';
import { Checkbox, Flex, Text, Tooltip, Box, Center, Spinner } from '@chakra-ui/react';
import { DITable } from 'plugins/DITable';
import TablePagination from 'plugins/DITable/Pagination';
import { getSelectedSegments, generateCategoryKeywords, getKeywordNames } from 'pages/Keywords/helpers/dataGenerators';
import { ChevronDownIcon, ChevronRightIcon, InfoIcon } from '@chakra-ui/icons';
import { linkStyle } from 'shared/styles';
import { Column, useFilters, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';
import { areChildrenChecked, checkCategories, toggleCategory } from 'pages/Keywords/helpers/handlers';
import { useTranslation } from 'helpers/languages/translations';
import { ICategory, IUserInfo } from 'types';
import useKeywordCategories from './useKeywordCategories';
import CategoriesModal from './CategoriesModal';
import CategoriesToolbar from './CategoriesToolbar';

const KEYWORD_COLUMN_LIMIT = 5;

const MKeywordsViewer: FC<{ readonly: boolean; userInfo?: IUserInfo }> = ({ readonly, userInfo }) => {
    const {
        handleCloseAddAndUpdateModal,
        handleEditCategories,
        modalType,
        keywordFilter,
        setKeywordFilter,
        isLoading,
        isUpdateCategoryLoading,
        updateInitialRef,
        data,
        setData,
        keywordsToSelect,
        handleViewKeywords,
        setKeywordsToSelect,
        setKeywordsByType,
        filteredKeywords,
        visibleKeywords,
        handleSubmit,
        isOpen,
        treeOpenByDefault,
        setTreeOpenByDefault,
        keywords,
        defaultColumn,
        sortTypes,
        filters,
        setFilters,
        isExportLoading,
        isFetching,
        handleExportKeywordCategories,
        languages,
    } = useKeywordCategories({ readonly, userInfo });
    const isToolbarDisabled = !getSelectedSegments(data).map((e) => Number(e.id)).length;
    const translate = useTranslation();

    const columns: Readonly<Column[]> = useMemo(() => {
        const tableColumns: readonly (Column | null)[] = [
            {
                Header: (
                    <Text as="span" display="flex" alignItems="center">
                        {translate('categories_keywords.column.category')}
                        <Tooltip
                            label={translate('categories_keywords.info.header.category')}
                            aria-label="Categories tooltip"
                            placement="bottom-end"
                        >
                            <InfoIcon color="blue.500" cursor="pointer" ml="5px" />
                        </Tooltip>
                    </Text>
                ),
                accessor: 'name',
                sortType: 'caseInsensitive',
                filter: (rows, columnIds: string[], filterValue: string) => {
                    return rows?.filter((row) => {
                        const rowToFilter = row.original as ICategory;
                        let foundSegment = false;
                        if (rowToFilter.name?.toLowerCase().includes(filterValue?.toLowerCase())) {
                            return true;
                        }
                        if (rowToFilter.subRows) {
                            if (
                                rowToFilter?.subRows?.some((subRow) => {
                                    return subRow.name?.toLowerCase().includes(filterValue?.toLowerCase());
                                })
                            )
                                return true;

                            rowToFilter.subRows.forEach((subRow) => {
                                if (subRow.subRows) {
                                    if (
                                        subRow?.subRows?.some((subSubRow) => {
                                            return subSubRow.name?.toLowerCase().includes(filterValue?.toLowerCase());
                                        })
                                    )
                                        foundSegment = true;
                                }
                            });
                        }
                        return foundSegment;
                    });
                },
                width: '50%',
                Cell: ({ cell: { row } }) => {
                    const rowValues = row.original as ICategory;

                    const textStyles = { ml: '0px' };
                    if (rowValues.level === 'category') {
                        textStyles.ml = '0px';
                    } else if (rowValues.level === 'subcategory') {
                        textStyles.ml = '20px';
                    } else if (rowValues.level === 'segment') {
                        textStyles.ml = '40px';
                    }

                    let rowIcon = null;
                    if (rowValues.subRows.length) {
                        if (rowValues.isExpanded) {
                            rowIcon = <ChevronDownIcon onClick={() => toggleCategory(rowValues, setData)} />;
                        } else {
                            rowIcon = <ChevronRightIcon onClick={() => toggleCategory(rowValues, setData)} />;
                        }
                    }

                    const childrenChecked = rowValues.subRows && areChildrenChecked(rowValues.subRows);
                    const checkboxStyles = {
                        mr: '2',
                        ...(readonly ? { display: 'none' } : {}),
                    };

                    return (
                        <Flex alignItems={'center'}>
                            <Text {...textStyles}>
                                <span
                                    style={{
                                        marginRight: '10px',
                                        cursor: 'pointer',
                                        height: '10px',
                                        minWidth: '20px',
                                    }}
                                >
                                    {rowIcon}
                                </span>
                                <Checkbox
                                    {...checkboxStyles}
                                    onChange={() => {
                                        checkCategories(rowValues, setData);
                                    }}
                                    isChecked={rowValues.checked}
                                    isIndeterminate={childrenChecked && !rowValues.checked}
                                ></Checkbox>
                                {rowValues.name}
                            </Text>
                        </Flex>
                    );
                },
            },
            {
                Header: (
                    <Text as="span" display="flex" alignItems="flex-start" minH="50px">
                        {translate('keyword.column.keyword')}
                        <Tooltip
                            label={translate('categories_keywords.info.header.keyword')}
                            aria-label="Keywords tooltip"
                        >
                            <InfoIcon color="blue.500" cursor="pointer" ml="5px" />
                        </Tooltip>
                    </Text>
                ),
                accessor: 'keywords',
                disableFilters: true,
                disableSortBy: true,
                width: '50%',
                Cell: ({ cell: { row } }) => {
                    const rowValues = row.original as ICategory;
                    const keywordsList = getKeywordNames(keywords?.keywords, generateCategoryKeywords(rowValues));
                    const cellProps = keywordsList.length
                        ? {
                              ...linkStyle,
                              textDecoration: 'none',
                              onClick: () => {
                                  handleViewKeywords(generateCategoryKeywords(rowValues));
                              },
                          }
                        : {};
                    return (
                        <Box height={35}>
                            <Text {...cellProps}>
                                <>
                                    {keywordsList.length ? (
                                        <>
                                            {keywordsList
                                                .slice(0, KEYWORD_COLUMN_LIMIT)
                                                .map((keyword) => keyword)
                                                .join(', ')}
                                            {keywordsList.length > KEYWORD_COLUMN_LIMIT && (
                                                <Text as="span">
                                                    {`... (${keywordsList.length - KEYWORD_COLUMN_LIMIT} ${(
                                                        (keywordsList.length - KEYWORD_COLUMN_LIMIT > 1
                                                            ? translate('product_keyword.column.kewyords')
                                                            : translate('product_keyword.column.kewyord')) as string
                                                    ).toLocaleLowerCase()})`}
                                                </Text>
                                            )}
                                        </>
                                    ) : (
                                        <Text as="span">-</Text>
                                    )}
                                </>
                            </Text>
                        </Box>
                    );
                },
            },
        ];

        const filteredColumns = tableColumns.filter((e): e is Column => e !== null);
        return filteredColumns;
    }, [data, userInfo, languages]);

    const tableInstance = useTable(
        { columns, data, defaultColumn, sortTypes, initialState: { filters } },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination,
    );

    useEffect(() => {
        setFilters(tableInstance.state.filters);
    }, [tableInstance.state.filters]);

    return (
        <Box mt="16px">
            <CategoriesToolbar
                handleEditCategories={handleEditCategories}
                isDisabled={isToolbarDisabled || readonly}
                handleExportKeywordCategories={handleExportKeywordCategories}
                isExportLoading={isExportLoading}
                isFetching={isFetching}
            />
            {isLoading ? (
                <Center w="100%" h="200px" bg="white" border="1px solid" borderColor="gray.100">
                    <Spinner />
                </Center>
            ) : (
                <>
                    <DITable {...tableInstance} isPagination isHeaderFixed />
                    <TablePagination tableInstance={tableInstance} />
                </>
            )}

            <CategoriesModal
                updateInitialRef={updateInitialRef}
                isOpen={isOpen}
                treeOpenByDefault={treeOpenByDefault}
                setTreeOpenByDefault={setTreeOpenByDefault}
                handleCloseAddAndUpdateModal={handleCloseAddAndUpdateModal}
                modalType={modalType}
                keywordFilter={keywordFilter}
                setKeywordFilter={setKeywordFilter}
                keywordsToSelect={keywordsToSelect}
                setKeywordsToSelect={setKeywordsToSelect}
                setKeywordsByType={setKeywordsByType}
                isUpdateCategoryLoading={isUpdateCategoryLoading}
                handleSubmit={handleSubmit}
                filteredKeywords={filteredKeywords}
                visibleKeywords={visibleKeywords}
                languages={languages}
            />
        </Box>
    );
};

export default MKeywordsViewer;
